import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { Badge } from "@/components/ui/Badge";
import { formatDateTime } from "@/lib/format";
import { ConfigVersionEditor } from "./components/ConfigVersionEditor";
import type { ConfigVersion } from "@/types/admin";

export function ConfigurationPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<ConfigVersion | null>(null);

  const versions = useQuery({
    queryKey: ["config-versions"],
    queryFn: AdminAPI.listConfigVersions,
  });

  const publish = useMutation({
    mutationFn: ({ values, description }: { values: Record<string, unknown>; description: string }) =>
      AdminAPI.publishConfig(values, description),
    onSuccess: (r) => {
      toast.success(`Configuration v${r.version} published successfully`);
      qc.invalidateQueries({ queryKey: ["config-versions"] });
      setSelected(null);
    },
  });

  const active = versions.data?.find((v) => v.is_active) ?? null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Configuration</h1>
        <p className="text-sm text-surface-500 mt-1">
          Immutable, version-pinned business configuration. Every change creates a new version.
          Existing Club Entries continue to use their pinned version — never affected.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Version History</CardTitle>
            <History className="h-4 w-4 text-surface-400" />
          </CardHeader>
          <CardBody className="p-0">
            {versions.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : versions.data && versions.data.length > 0 ? (
              <ul className="divide-y divide-surface-100">
                {versions.data.map((v) => (
                  <li key={v.id}>
                    <button
                      onClick={() => setSelected(v)}
                      className={`w-full text-left px-5 py-3 hover:bg-surface-50 transition-colors ${
                        selected?.id === v.id ? "bg-brand-50" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-surface-900">v{v.version_number}</div>
                        {v.is_active && (
                          <Badge tone="success">
                            <CheckCircle2 className="h-3 w-3" /> Active
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-surface-500 mt-0.5 line-clamp-1">
                        {v.description || "No description"}
                      </div>
                      <div className="text-2xs text-surface-400 mt-1">
                        by {v.created_by} · {formatDateTime(v.created_at)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center text-sm text-surface-500">
                No versions yet. Publish the first one.
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {selected ? `Viewing v${selected.version_number}` : "Publish New Version"}
            </CardTitle>
            {selected && (
              <button onClick={() => setSelected(null)}
                      className="text-sm text-brand-600 hover:text-brand-700 font-medium">
                Start new draft
              </button>
            )}
          </CardHeader>
          <CardBody>
            {selected ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-2xs uppercase text-surface-500 font-semibold">Description</div>
                    <div className="text-surface-900">{selected.description || "—"}</div>
                  </div>
                  <div>
                    <div className="text-2xs uppercase text-surface-500 font-semibold">Effective</div>
                    <div className="text-surface-900">{formatDateTime(selected.effective_at)}</div>
                  </div>
                </div>
                <div>
                  <div className="text-2xs uppercase text-surface-500 font-semibold mb-2">Values</div>
                  <pre className="text-xs bg-surface-50 border border-surface-200 rounded-lg p-4 overflow-auto max-h-[500px] font-mono">
                    {JSON.stringify(selected.values ?? {}, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <ConfigVersionEditor
                initialValues={active?.values}
                onSubmit={(values, description) => publish.mutate({ values, description })}
                loading={publish.isPending}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
